import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  vaultCandidatesQueryOptions,
  vaultCandidatesQueryKey,
  type CredentialVaultScope,
} from "../../lib/credential-vault-scope";
import { useTRPC } from "@/trpc";
import { isReviewUploadBlocked } from "../../lib/review-upload-security";
import {
  pendingReviewDocumentIds,
  reviewSaveBlocker,
} from "./provider-credential-review-values";

const REVIEW_POLL_INTERVAL_MS = 2500;

export function useCredentialVaultReview({
  scope,
  reviewDocumentId,
}: {
  scope: CredentialVaultScope;
  reviewDocumentId?: string | null;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [uploadedIds, setUploadedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [omittedIds, setOmittedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [removedDocumentIds, setRemovedDocumentIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    pendingFileCount: 0,
  });
  const selectedIds = useMemo(() => {
    const ids = new Set(uploadedIds);
    if (reviewDocumentId) {
      ids.add(reviewDocumentId);
    }
    return ids;
  }, [uploadedIds, reviewDocumentId]);

  const query = useQuery({
    ...vaultCandidatesQueryOptions(trpc, scope),
    enabled: selectedIds.size > 0,
    refetchInterval: (current) =>
      pendingReviewDocumentIds({
        items: current.state.data ?? [],
        selectedIds,
        omittedIds,
      }).size > 0
        ? REVIEW_POLL_INTERVAL_MS
        : false,
  });
  // Membership comes from the exact upload action or selected review row, never
  // from sharing a type with an unrelated upload elsewhere in the queue.
  const items = useMemo(
    () => (query.data ?? []).filter((item) => selectedIds.has(item.documentId)),
    [query.data, selectedIds],
  );
  const blocker = reviewSaveBlocker({ items, selectedIds, omittedIds });
  const nonParsingBlocker = reviewSaveBlocker({
    items,
    selectedIds,
    omittedIds,
    skipParsing: true,
  });
  const parsingFileCount = pendingReviewDocumentIds({
    items,
    selectedIds,
    omittedIds,
  }).size;
  const keptItems = useMemo(
    () =>
      items.filter(
        (item) =>
          !omittedIds.has(item.documentId) && !isReviewUploadBlocked(item),
      ),
    [items, omittedIds],
  );

  async function filesUploaded(documentIds: string[]) {
    setUploadedIds((previous) => new Set([...previous, ...documentIds]));
    await queryClient.invalidateQueries({
      queryKey: vaultCandidatesQueryKey(trpc, scope),
    });
  }

  function setReviewDocumentKept(documentId: string, kept: boolean) {
    const item = items.find((item) => item.documentId === documentId);
    if (kept && item && isReviewUploadBlocked(item)) {
      return;
    }
    setOmittedIds((previous) => {
      const next = new Set(previous);
      if (kept) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }

  function setExistingDocumentKept(documentId: string, kept: boolean) {
    setRemovedDocumentIds((previous) => {
      const next = new Set(previous);
      if (kept) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }

  const reviewDocuments = Array.from(selectedIds, (documentId) => {
    const item = items.find((item) => item.documentId === documentId);
    return {
      documentId,
      attach:
        !omittedIds.has(documentId) && !(item && isReviewUploadBlocked(item)),
    };
  });
  const hasChanges =
    selectedIds.size > 0 ||
    removedDocumentIds.size > 0 ||
    uploadState.pendingFileCount > 0;

  return {
    items,
    keptItems,
    isParsingFiles: parsingFileCount > 0,
    parsingFileCount,
    selectedIds,
    omittedIds,
    removedDocumentIds,
    isUploadOpen,
    setUploadOpen,
    uploadState,
    setUploadState,
    filesUploaded,
    setReviewDocumentKept,
    setExistingDocumentKept,
    query,
    blocker,
    nonParsingBlocker,
    hasChanges,
    reviewDocuments,
  };
}
